import { doSignin, getLob, getLOBClientConfig, getUserFromToken, login, logoutUser, sendForgotPasswordOtp, setAuthContext, setDashboardCookies } from "./authenticationService";
import { LoginData, loginData } from "../types/types";
import { AuthClientConfig, DeviceInfo, FlowConfiguration, FormLoginData, LoginRequest, OtpLoginData, RefreshTokenRequest, RefreshTokenResponse, SalesCodeAuthClient } from "@salescode/salescode-auth-web-sdk";
import { EnvironmentRegistry } from "./registry/environmentRegistry";
import packageJson from '../../package.json'; 
import { ChannelkartNetworkGet, checkAndRefreshTokenIfNeeded } from "./networkService";




export async function getAuthServiceConfig(environment: string): Promise<AuthClientConfig> {
  const localStorageKey = 'authserviceconfig';
  const storedConfig = localStorage.getItem(localStorageKey);
  if (storedConfig) {
    return JSON.parse(storedConfig) as AuthClientConfig;
  } else {
    const newConfig = await createClientConfig(environment);
    localStorage.setItem(localStorageKey, JSON.stringify(newConfig));
    return newConfig;
  }
}

export async function createClientConfig(environment: string): Promise<AuthClientConfig> {
  const isAuthService = await getAuthServiceState();
  return {
    env: environment,
    url: isAuthService ? EnvironmentRegistry.getAuthUrl(environment) : undefined,
    app: "web_portal",
    tenant: getLob()
  };
}

let salesCodeAuthClient: SalesCodeAuthClient | null = null;
export async function getSalesCodeAuthClient(): Promise<SalesCodeAuthClient> {
  if (!salesCodeAuthClient) {
    const environment = EnvironmentRegistry.getEnvironment();
    const clientConfig = await getAuthServiceConfig(environment as string);
    salesCodeAuthClient = new SalesCodeAuthClient(clientConfig);
  }
  return salesCodeAuthClient;
}

export async function getAuthServiceState() {
  const lob = localStorage.getItem("lob") ? undefined : getLob();
  const clientConfig = await getLOBClientConfig(lob);
  const isAuthService = clientConfig?.find((config: { domainType: string }) => config.domainType === "authservice_configuration")?.domainValues?.find((item: { name: string }) => item.name === "authservice")?.value ?? null;
  return { isAuthService };
}

export async function getSinchOtpProvider() {
  const lob = localStorage.getItem("lob") ? undefined : getLob();
  const clientConfig = await getLOBClientConfig(lob);
  const otpProvider = clientConfig?.find((config: { domainType: string }) => config.domainType === "authentication_configuration")?.domainValues?.find((item: { name: string }) => item.name === "otpProvider")?.value ?? null;
  return { otpProvider };
}

export async function getFlows() {
  const client = await getSalesCodeAuthClient();
  const flows = await client.getFlows();
  if (flows.ssoConfigurations?.content[0]?.registrationId != null) {
    localStorage.setItem('registrationId', flows.ssoConfigurations?.content[0].registrationId);
  }
  return flows;
}

export async function loginByAuthService(userName: string, password: string, lob: string, isEndToEnd: boolean, flows: FlowConfiguration) {
  const response = await signIn(userName, password, lob, isEndToEnd, flows);
  localStorage.setItem("lob", lob);
  return response;
}

async function signIn(userName: string, password: string, lob: string, isEndToEnd: boolean, flows: FlowConfiguration) {
  const loginData = {
    loginId: userName,
    password: password,
    lob: lob,
    unlimitedExpiry: true,
  };
  return signinService(loginData, isEndToEnd, flows) as any;
}

export async function signinService(loginData: loginData, isEndToEnd: boolean, flows: FlowConfiguration) {
  if (isEndToEnd) {
    const client = await getSalesCodeAuthClient();
    const token = await client.generateE2EKey();
    loginData.secureToken = token;
    loginData.secureToken = await client.getEncryptedSecureToken(loginData.secureToken);
    localStorage.setItem("endToend", String(isEndToEnd));
    setEncryptionKey(token);
  }
  const response = await doSigninAuth(loginData, flows);
  return response;
}

export async function doSigninAuth(input: LoginData, flows: FlowConfiguration) {
  if (flows.providerName === "form") {
    return await doFormLogin(input);
  }
}

function setEncryptionKey(token: string) {
  sessionStorage.setItem("secureToken", token);
}

async function doFormLogin(input: LoginData) {

  const deviceInfo: DeviceInfo = {
    deviceId: input.deviceId || getMachineId(),
    platformType: "web",
    platformVersion: navigator.userAgent,
    appName: "web_portal",
    appVersion: packageJson.version,
    deviceName: getDeviceName(),
    active: true
  }

  const formLoginData: FormLoginData = {
    loginId: input.loginId,
    password: input.password,
    loginContext: "",
    deviceInfo: deviceInfo,
    secureToken: input.secureToken
  }

  const loginRequest: LoginRequest = {
    method: "form",
    step: "login",
    data: [formLoginData]
  }
  const client = await getSalesCodeAuthClient();

  const response = await client.executeLogin(loginRequest);
  setAuthServiceAccessExpiry(Number(response.tokenInformation?.accessTokenExpiresIn));
  if (input.secureToken) {
    delete input.secureToken;
  }
  if (response.loginContext) {
    return response;
  }

  return await getAuthUser(response.tokenInformation?.accessToken as string);
}

export async function getAuthUser(token: string) {
  const res = await getUserFromToken(token);
  const cktResponse = {
    data: {
      user: {
        ...res
      },
      token: token,
    },
    status: 200
  }
  return cktResponse;
}

function getDeviceInfo(): DeviceInfo {
  return {
    deviceId: getMachineId(),
    platformType: "web",
    platformVersion: navigator.userAgent,
    appName: "web_portal",
    appVersion: packageJson.version,
    deviceName: getDeviceName(),
    active: true
  };
}

export async function sendOtp(userName: string, password: string, lob: string, isEndToEnd: boolean, phase: string, loginContext?: string) {
  const input = {
    loginId: userName,
    password: password,
    lob: lob,
    unlimitedExpiry: true,
    secureToken: ""
  };

  const otpProvider = await getSinchOtpProvider();
  const isSinch = otpProvider.otpProvider === "sinch" ? true : false;

  if (isEndToEnd) {
    const client = await getSalesCodeAuthClient();
    const token = await client.generateE2EKey();
    input.secureToken = token;
    input.secureToken = await client.getEncryptedSecureToken(input.secureToken);
    localStorage.setItem("endToend", String(isEndToEnd));
    setEncryptionKey(token);
  }

  const isVerify = phase === "verify-otp";

  const otpLogindata: OtpLoginData = {
    loginId: input.loginId,
    ...(isVerify && { otp: password }),
    loginContext: loginContext,
    deviceInfo: getDeviceInfo(),
    ...(isVerify && isEndToEnd && { secureToken: input.secureToken }),
    ...(isVerify && isSinch && { providerId: sessionStorage.getItem("providerToken") as string }),
    ...(isSinch && { countryCode: "65" })
  };
  const loginRequest: LoginRequest = {
    method: "otp",
    step: phase,
    data: [otpLogindata]
  }
  const client = await getSalesCodeAuthClient();
  const response = await client.executeLogin(loginRequest);
  setAuthServiceAccessExpiry(Number(response.tokenInformation?.accessTokenExpiresIn));

  if (isSinch && !isVerify) {
    sessionStorage.setItem("providerToken", response.response?.message as string)
  }
  if (isSinch && isVerify) {
    sessionStorage.removeItem("providerToken");
  }
  return isVerify
    ? await getAuthUser(response.tokenInformation?.accessToken as string)
    : response;
}

function getMachineId() {
  let machineId = localStorage.getItem('MachineId');
  if (!machineId) {
    machineId = crypto.randomUUID();
    localStorage.setItem('MachineId', machineId);
  }
  return machineId;
}

export async function getAuthRefreshToken() {
  const isEndToEnd = localStorage.getItem("endToend");
  const client = await getSalesCodeAuthClient();
  let secureToken;
  if (isEndToEnd) {
    secureToken = await client.generateE2EKey();
    setEncryptionKey(secureToken);
    secureToken = await client.getEncryptedSecureToken(secureToken);
  }
  const refreshTokenRequest: RefreshTokenRequest = {
    refreshToken: "",
    ...(isEndToEnd === "true" && secureToken && { secureToken: secureToken })
  }
  if (sessionStorage.getItem("access_token")) {
    const response = await client.refreshToken(refreshTokenRequest);
    if (response) {
      return response;
    }
  }
  else {
    await logoutUser(false, true);
    throw new Error("You have been logged out due to security reasons")
  }
}

export function setAuthServiceAccessExpiry(tokenExpiry: number) {
  const expiryTime = Date.now() + tokenExpiry * 1000;
  localStorage.setItem("access_token_expiry", expiryTime.toString());
}

function getDeviceName() {
  const userAgent = navigator.userAgent || "unknown";
  const platform = navigator.platform || "unknown platform";

  return `${platform} - ${userAgent}`;
}


export async function getSSORedirectUrl(registrationId: string, provider: string) {
  const client = await getSalesCodeAuthClient();
  let requestParams: Record<string, string> = {};
  if (provider === "SAML") {
    requestParams = {
      app: "web_portal",
      targetUrl: window.location.origin + "/dashboardLogin",
      tenant: getLob()
    };
  }
  return await client.getAuthorizationUrl(registrationId, requestParams);
}

export async function loginByAuthServiceSSO(param: any) {
  if (param != null && param['sessionId']!=='error') {
    const client = await getSalesCodeAuthClient();
    const registrationId = localStorage.getItem('registrationId');
    if (registrationId) {
      const res = await client.authorizeAndGenerateToken(registrationId, param, getDeviceInfo());
      await setAuthServiceAccessExpiry(Number(res.tokenInformation?.accessTokenExpiresIn));
      return await getAuthUser(res.tokenInformation?.accessToken as string);
    }
  }
  else {
    return;
  }
}

export async function setAccessTokenInContext(updatedTokenInformation: string) {
  sessionStorage.setItem("access_token", updatedTokenInformation);
}

export async function logoutTokenAuthService(token: string, setflag?: boolean) {
  localStorage.removeItem("access_token_expiry");
  localStorage.removeItem("endToend");
  localStorage.removeItem("secureToken");
  localStorage.removeItem("authserviceconfig");
  sessionStorage.removeItem("secureToken");
  
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
    if (name.startsWith("accessToken")) {
      // Clear cookie with both path and current domain
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${window.location.hostname}`;
    }
  }

  if (!setflag && token !== "") {
    const client = await getSalesCodeAuthClient();
    await checkAndRefreshTokenIfNeeded();
    try {
      await client.logout(token);
    } catch (error) {
      window.location.replace("/dashboardLogin")
      throw new Error("You have been logged out due to security reasons")
    }
  }
}
  

export async function getChannelDivision(designations: string[], token: string): Promise<boolean> {
    try {
        const encodedDesignations = designations.map(encodeURIComponent).join(",");
        const url = `/v1/divisions?filter=divisionName[in]:[${encodedDesignations}]`;
        
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        };

        const divisionData = await ChannelkartNetworkGet(url, undefined, headers);
        return divisionData?.data?.features?.some((node: any) => node?.channelDivision === true) ?? false;

    } catch (error) {
        console.error("Error fetching division data:", error);
        return false; // Fail gracefully
    }
}
